package io.github.retar.portfolio.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initOn_device_tts_sherpa_onnxPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("on_device_tts_sherpa_onnx.md", FrontMatterElement.Builder { addScalar("title", "Neural voices on an e-ink reader: on-device TTS in Parrot with sherpa-onnx"); addScalar("description", "How Parrot reads any ebook aloud with Kokoro and Supertonic running on the phone: downloading 150 MB voices safely, preparing chapters ahead, and the lambda that crashed every synthesis."); addScalar("date", "2026-10-13"); addScalar("cover", "/articles/on_device_tts.jpeg"); }))
}

@Page("/blog/on-device-neural-tts-android")
@Layout(".pages.MarkdownPage")
@Composable
fun On_device_tts_sherpa_onnxPage() {
    org.jetbrains.compose.web.dom.H1(attrs = { id("neural-voices-on-an-e-ink-reader-on-device-tts-in-parrot-with-sherpa-onnx") }) {
        org.jetbrains.compose.web.dom.Text("Neural voices on an e-ink reader: on-device TTS in Parrot with sherpa-onnx")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Parrot started as a read-along app. You give it an ebook and the matching audiobook, and it highlights each sentence as the narrator reads it. But most ebooks don't have an audiobook. So I wanted Parrot to be able to read any book aloud, with a voice that doesn't sound like a GPS from 2009, without sending the text to a server.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("That means running a neural text-to-speech model on the device. Here's how it works in Parrot today, what the numbers look like on real hardware, and the bugs that cost me the most time.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-engine-sherpa-onnx-two-models") }) {
        org.jetbrains.compose.web.dom.Text("The engine: sherpa-onnx, two models")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Parrot uses ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/k2-fsa/sherpa-onnx") {
            org.jetbrains.compose.web.dom.Text("sherpa-onnx")
        }
        org.jetbrains.compose.web.dom.Text(", which wraps ONNX Runtime and ships ready-made TTS pipelines (phonemizer, acoustic model, vocoder) behind one ")
        io.github.retar.portfolio.components.widgets.InlineCode("""OfflineTts""")
        org.jetbrains.compose.web.dom.Text(" class. It comes as an Android AAR with JNI bindings, so the Kotlin side stays small.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("There are two neural voice families:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Kokoro")
            }
            org.jetbrains.compose.web.dom.Text(" (Kokoro-82M, the INT8 multi-language pack). Parrot exposes the 28 US and UK English speakers. A UK voice only needs a different eSpeak phonemizer language per call, not a second engine.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.B {
                org.jetbrains.compose.web.dom.Text("Supertonic 3")
            }
            org.jetbrains.compose.web.dom.Text(", a flow-matching model by Supertone. It is faster and sounds great, but its license requires users to accept terms before downloading, and the app labels its speech as AI-generated.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Next to those sits the Android system ")
        io.github.retar.portfolio.components.widgets.InlineCode("""TextToSpeech""")
        org.jetbrains.compose.web.dom.Text(" engine, wrapped behind the same synthesizer interface. A small router picks the engine by voice id, so the rest of the reader never knows which one is talking.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Generation itself is one suspending call. It runs on ")
        io.github.retar.portfolio.components.widgets.InlineCode("""Dispatchers.IO""")
        org.jetbrains.compose.web.dom.Text(" behind a mutex, because a native engine instance isn't safe to call from two threads at once:")
    }
    io.github.retar.portfolio.components.widgets.CodeBlock("""val audio = generationMutex.withLock {
    withContext(Dispatchers.IO) {
        val activeEngine = engine
            ?: throw CancellationException("Kokoro engine released")
        activeEngine.generateWithConfigAndCallback(
            text,
            GenerationConfig(
                sid = speakerId,
                speed = speed,
                extra = mapOf("lang" to kokoroPhonemizerLanguage(voiceId)),
            ),
            neuralGenerationCallback {
                requestJob?.isActive == false ||
                        cancellationGeneration.get() != requestGeneration
            },
        )
""", lang = "kotlin")
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("That callback is how cancellation reaches native code: sherpa-onnx calls it with each batch of samples, and returning ")
        io.github.retar.portfolio.components.widgets.InlineCode("""1""")
        org.jetbrains.compose.web.dom.Text(" aborts generation. It is also the line that crashed the app on every single synthesis. More on that below.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("getting-150-mb-of-model-onto-a-phone") }) {
        org.jetbrains.compose.web.dom.Text("Getting 150 MB of model onto a phone")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The Kokoro pack is about 149 MB and Supertonic about 145 MB. Neither ships in the APK. Voices are downloaded on demand from GitHub Releases, described by a ")
        io.github.retar.portfolio.components.widgets.InlineCode("""manifest.json""")
        org.jetbrains.compose.web.dom.Text(" that lists every file with its URL, size and SHA-256.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The manifest isn't signed, so the downloader treats it as untrusted input:")
    }
    org.jetbrains.compose.web.dom.Ul {
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("It only accepts URLs under the project's own releases path, and follows redirects by hand, requiring HTTPS on ")
            io.github.retar.portfolio.components.widgets.InlineCode("""github.com""")
            org.jetbrains.compose.web.dom.Text(" or ")
            io.github.retar.portfolio.components.widgets.InlineCode("""*.githubusercontent.com""")
            org.jetbrains.compose.web.dom.Text(" at every hop.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("It rejects path traversal in file names and caps file and model sizes.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Every file is verified against its SHA-256 before the new version becomes active. The switch is a marker file that moves only after the whole set has been verified, so a half-downloaded voice can never be loaded.")
        }
        org.jetbrains.compose.web.dom.Li {
            org.jetbrains.compose.web.dom.Text("Downloads resume with HTTP ")
            io.github.retar.portfolio.components.widgets.InlineCode("""Range""")
            org.jetbrains.compose.web.dom.Text(" into ")
            io.github.retar.portfolio.components.widgets.InlineCode(""".part""")
            org.jetbrains.compose.web.dom.Text(" files, retry with backoff, and check free disk space before starting.")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The biggest win here was boring. The first version downloaded the upstream ")
        io.github.retar.portfolio.components.widgets.InlineCode(""".tar.bz2""")
        org.jetbrains.compose.web.dom.Text(" and unpacked it on the device. On an Onyx Boox Nova Air, an Android e-ink reader, the tap-to-ready time was ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("135.9 seconds")
        }
        org.jetbrains.compose.web.dom.Text(", and ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("124 of those seconds were single-threaded bzip2")
        }
        org.jetbrains.compose.web.dom.Text(". Switching to plain per-file downloads, with only the small eSpeak data still zipped, cut it to ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("21.6 seconds")
        }
        org.jetbrains.compose.web.dom.Text(". The network was never the bottleneck.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-real-problem-e-readers-are-slow") }) {
        org.jetbrains.compose.web.dom.Text("The real problem: e-readers are slow")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Speech synthesis speed is best expressed as a real-time factor, ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("r")
        }
        org.jetbrains.compose.web.dom.Text(": seconds spent generating per second of audio. Below 1.0 you can synthesize live. Above 1.0, playback eventually catches up with generation and stops to wait.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("I benchmarked both models on two devices:")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Device")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Supertonic (8 steps)")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("Kokoro")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Onyx Boox Nova Air (e-ink)")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("r ≈ 2.3")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("r ≈ 5")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("Xiaomi, MediaTek MT6991")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("r ≈ 0.6")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("r ≈ 1.15")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Two takeaways. First, even a fast 2026 phone can't run Kokoro in real time. Second, on the e-reader, the device Parrot cares most about, nothing is real-time. Ten minutes of chapter at r = 2.3 needs about six minutes of audio generated before playback can start and never stall.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Threads didn't save me either. Going from 2 to 4 threads gave 7 to 10 percent, and 8 threads was no better than 4 and sometimes worse. Parrot uses ")
        io.github.retar.portfolio.components.widgets.InlineCode("""availableProcessors().coerceIn(2, 4)""")
        org.jetbrains.compose.web.dom.Text(". Fewer Supertonic flow steps help more: going from 8 to 6 steps is 20 to 25 percent faster, and 4 steps about 45 percent. But in a listening test 4 steps sounded clearly worse, so the quality floor is 6, and even 4 steps stays above r = 1 on the e-reader.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("So live synthesis alone could never be the whole answer.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("preparing-chapters-ahead-of-time") }) {
        org.jetbrains.compose.web.dom.Text("Preparing chapters ahead of time")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Parrot can ")
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("prepare")
        }
        org.jetbrains.compose.web.dom.Text(" a chapter: synthesize every sentence in the background and store it, so playback later is just file playback.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("What gets prepared.")
        }
        org.jetbrains.compose.web.dom.Text(" The reader splits the chapter into sentences, and long sentences into chunks of at most 280 characters (with care for abbreviations, decimals and ellipses). Preparation starts at the sentence you're on, runs to the end of the chapter, then wraps around to the beginning, so the part you're about to hear is ready first.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("The queue survives the app.")
        }
        org.jetbrains.compose.web.dom.Text(" Jobs live in a SQLDelight table and run in a ")
        io.github.retar.portfolio.components.widgets.InlineCode("""dataSync""")
        org.jetbrains.compose.web.dom.Text(" foreground service, so killing the app doesn't lose them. The notification never shows the book or chapter title on the lock screen.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Live speech always wins.")
        }
        org.jetbrains.compose.web.dom.Text(" There is one synthesis permit. When you press play while a chapter is preparing in the background, your sentence jumps the queue:")
    }
    io.github.retar.portfolio.components.widgets.CodeBlock("""withContext(NonCancellable) {
    mutex.withLock {
        if (request.granted) {
            val next = if (live.isNotEmpty()) live.removeFirst() else background.removeFirstOrNull()
            if (next == null) busy = false else {
                next.granted = true
                next.signal.complete(Unit)
            }
""", lang = "kotlin")
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The gate never interrupts a native call already in progress. It just decides who goes next.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Storage is AAC, not WAV.")
        }
        org.jetbrains.compose.web.dom.Text(" Each sentence is encoded with ")
        io.github.retar.portfolio.components.widgets.InlineCode("""MediaCodec""")
        org.jetbrains.compose.web.dom.Text(" to AAC-LC, mono, about 48 kbit/s, in an ")
        io.github.retar.portfolio.components.widgets.InlineCode(""".m4a""")
        org.jetbrains.compose.web.dom.Text(". That's roughly seven times smaller than the WAV. The cost is 87 to 127 ms of encoder padding per sentence. I tried remuxing with encoder-delay metadata to remove it, that didn't work, and at sentence boundaries the padding reads as a natural pause. Prepared audio is capped at 1 GiB.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("File names say nothing.")
        }
        org.jetbrains.compose.web.dom.Text(" Each clip is keyed by ")
        io.github.retar.portfolio.components.widgets.InlineCode("""sha256(voiceId | modelVersion | rate | pitch | text)""")
        org.jetbrains.compose.web.dom.Text(", and folders are hashes of the server, book and chapter. Nothing in the file system reveals what you're reading. A side effect is that changing the voice or speed makes a clean cache miss instead of playing stale audio.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("When playback needs a sentence, it looks in the prepared store first, then a small live WAV cache, and only then synthesizes.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("playback-and-highlighting") }) {
        org.jetbrains.compose.web.dom.Text("Playback and highlighting")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Playback is Media3. Every sentence is one ExoPlayer ")
        io.github.retar.portfolio.components.widgets.InlineCode("""MediaItem""")
        org.jetbrains.compose.web.dom.Text(", appended to the playlist as soon as its audio exists. The engine keeps four sentences synthesized ahead.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Highlighting falls out of that structure. Each item's media id is ")
        io.github.retar.portfolio.components.widgets.InlineCode("""tts:<book>:<sentenceIndex>""")
        org.jetbrains.compose.web.dom.Text(". When ExoPlayer moves to the next item, Parrot parses the index back and highlights that sentence in the reader, turning the page if needed. There is no forced alignment or word timing, just one sentence per media item. That's coarser than word-level highlighting, but it's robust and it costs nothing at runtime.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-bugs-that-cost-the-most") }) {
        org.jetbrains.compose.web.dom.Text("The bugs that cost the most")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("A lambda that killed the process (TTS-F22).")
        }
        org.jetbrains.compose.web.dom.Text(" The cancellation callback above was originally a plain Kotlin lambda. Every Kokoro synthesis crashed the app with no Kotlin stack trace. sherpa-onnx's native code looks up the callback method by its exact JNI signature, ")
        io.github.retar.portfolio.components.widgets.InlineCode("""invoke([F)Ljava/lang/Integer;""")
        org.jetbrains.compose.web.dom.Text(". A Kotlin lambda compiles to a synthetic class that only has the erased bridge ")
        io.github.retar.portfolio.components.widgets.InlineCode("""invoke(Object)Object""")
        org.jetbrains.compose.web.dom.Text(", so the lookup failed and ART aborted the process. The fix is an explicit object, which declares the specialized method:")
    }
    io.github.retar.portfolio.components.widgets.CodeBlock("""internal fun neuralGenerationCallback(shouldCancel: () -> Boolean): (FloatArray) -> Int =
    object : (FloatArray) -> Int {
        override fun invoke(samples: FloatArray): Int = if (shouldCancel()) 1 else 0
    }
""", lang = "kotlin")
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A host test now guards it, and an R8 keep rule makes sure the minifier doesn't undo it. If you call into a JNI library that takes a Kotlin function type, check how it looks the method up.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("The 5 ms race (TTS-F26).")
        }
        org.jetbrains.compose.web.dom.Text(" Narration sometimes just went silent. A sentence appended about 5 ms before ExoPlayer reported reaching the end of the queue was never played: the player had already decided it was done. The engine now notices this and restarts the next sentence itself.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Chapter handover (TTS-F30).")
        }
        org.jetbrains.compose.web.dom.Text(" At the end of a chapter, the engine stopped the playback service and started it again for the next chapter. But a foreground service started while the app is in the background can't be promoted to the foreground on modern Android, so the system stopped it for being idle. Now the service and its player survive the handover, and only the engine's state resets.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.B {
            org.jetbrains.compose.web.dom.Text("Warm-up.")
        }
        org.jetbrains.compose.web.dom.Text(" The first sentence after loading a model was always slow, because ONNX Runtime does one-time initialization on the first run. Parrot now synthesizes the text ")
        io.github.retar.portfolio.components.widgets.InlineCode(""""a"""")
        org.jetbrains.compose.web.dom.Text(" once right after loading, so that cost lands while you're still choosing a voice.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-s-next") }) {
        org.jetbrains.compose.web.dom.Text("What's next")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The iPhone version. A spike ran Kokoro in the iOS simulator with sherpa-onnx's static XCFrameworks. It works, but it adds 38 MB to the app, takes 4.5 seconds for a cold first synthesis, and peaks at 487 MB of memory. Getting that down is the next problem, and probably the next note.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The lessons so far: measure on the slowest device you care about, because that's where the design decisions get made. Treat downloaded models like any other untrusted input. And when a native library calls back into Kotlin, check exactly which method it's looking for.")
    }
}
